import { useState, type ReactElement } from "react";
import { Popover } from "@base-ui/react/popover";
import { useQueryClient } from "@tanstack/react-query";
import { AgentCopyButton } from "../../../../../packages/design/src/AgentCopyButton";
import { judgeOnDevicePrompt } from "../../../../../packages/contracts/src/local-prompt";
import { api, ApiError } from "../../api";
import { Button } from "../ui/button";

export type ReportTarget = { methodId: string; methodName: string; device: boolean; runId: string; runCreatedAt?: string; stepId?: string };

/**
 * ⚑ on a run: the person marks its result wrong, with optional words. A wrong judgment makes Method try a tested fix in
 * the background. A Method that keeps run content on the computer improves there, through the agent.
 */
export function ReportPopover({ target, trigger, title }: { target: ReportTarget; trigger: ReactElement; title?: string }) {
  const [note, setNote] = useState("");
  const [state, setState] = useState<{ status: "idle" | "busy" } | { status: "done"; held: boolean } | { status: "error"; message: string }>({ status: "idle" });
  const client = useQueryClient();
  async function judge() {
    setState({ status: "busy" });
    try {
      const { held } = await api.judgeRun(target.runId, { verdict: "wrong", ...(note.trim() ? { note: note.trim() } : {}), ...(target.stepId ? { step_id: target.stepId } : {}) });
      setState({ status: "done", held });
      await Promise.all([client.invalidateQueries({ queryKey: ["inbox"] }), client.invalidateQueries({ queryKey: ["workflows"] }), client.invalidateQueries({ queryKey: ["run-judgments", target.runId] })]);
    } catch (error) {
      setState({ status: "error", message: error instanceof ApiError || error instanceof Error ? error.message : "The judgment was not saved." });
    }
  }
  const when = target.runCreatedAt ? new Date(target.runCreatedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
  const heading = title ?? "What is wrong with this result?";
  return <Popover.Root onOpenChange={open => { if (!open) { setNote(""); setState({ status: "idle" }); } }}>
    <Popover.Trigger render={trigger} />
    <Popover.Portal>
      <Popover.Positioner side="bottom" align="end" sideOffset={6} collisionPadding={12} className="run-pop-positioner">
        <Popover.Popup className="run-pop" aria-label={heading}>
          <div className="pp-top"><b>{heading}</b><Popover.Close className="pp-x" aria-label="Close">×</Popover.Close></div>
          <p className="pp-line pp-faint">{target.methodName}{when ? ` · run of ${when}` : ""}</p>
          {state.status === "done" ? <p className="pp-line" role="status">{state.held ? "A teammate judged this result differently. Nothing is fixed until one of you keeps a judgment on the run page." : "Marked as wrong. This correction will be fixed the next time this Method runs on a computer with Claude Code or Codex."}</p> : <>
            <label className="pp-note"><textarea rows={3} value={note} onChange={event => setNote(event.target.value)} maxLength={4000} autoFocus
              aria-label="What is wrong, in your words" placeholder="Say what it should have done (optional)." /></label>
            {target.device && <p className="pp-line">This Method keeps run content on your computer, so Method cannot read this run. The next run of this Method on your computer fixes it there.</p>}
            {state.status === "error" && <p className="pp-error" role="alert">{state.message}</p>}
            <div className="pp-act">
              {target.device
                ? <AgentCopyButton text={judgeOnDevicePrompt(target.methodId, window.location.origin, note || "(say what is wrong)", target.methodName, target.runId)} label="Copy fix prompt for your agent" disabled={!note.trim()} />
                : <Button type="button" onClick={() => void judge()} disabled={state.status === "busy"}>{state.status === "busy" ? "Saving…" : "Mark as wrong"}</Button>}
            </div>
          </>}
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  </Popover.Root>;
}
